/* eslint-disable max-lines
-- Disabled to set CI to fail on this issue on new files, PR #8526 */
import { createRef, useEffect, useMemo, useRef, useState } from "react";
import { defer, delay, last, omit, sortBy, uniqBy } from "lodash";
import { useHistory } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { getVerticalHeightUnit } from "@/react/helpers/browserHelpers";
import { getSpaceNotificationDetail } from "@/react/helpers/spaceHelpers";
import { useMarkChatRoomAsRead } from "@/react/hooks/chatsV2/useMarkChatRoomAsRead";
import { useScrollInfo } from "@/react/hooks/chatsV2/useScrollInfo";
import { useSmOrMdScreenMediaQuery } from "@/react/hooks/useMediaQuery";
import type { ChatRoom } from "@/react/types/Chat/ChatRoom";
import type { ChatRoomMessage } from "@/react/types/Chat/ChatRoomMessage";
import {
  useCurrentSpaceContext,
  usePunditUserContext,
} from "@circle-react/contexts";
import { useSpaceNotificationsState } from "@circle-react/contexts/sidebarNotificationsContext";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import {
  useBookmarkToggle,
  useCanSendMessage,
  useChatMessagesV2Api,
  useChatParticipantsApi,
  useUpdateMissingParticipants,
} from "@circle-react/hooks/chatsV2";
import {
  useChatRoomStoreActions,
  useChatRoomStoreMessages,
  useChatRoomStoreStates,
} from "@circle-react/hooks/chatsV2/store";
import { useChatRoomContext } from "@circle-react/hooks/chatsV2/useChatRoomContext";
import { useThreads } from "@circle-react/hooks/chatsV2/useThreads";
import { useHideHelpscout } from "@circle-react/hooks/useHideHelpscout";
import { useSpaceSidebarNotifications } from "@circle-react/hooks/useSpaceSidebarNotifications";
import { SkeletonLoader } from "@circle-react-uikit/SkeletonLoader";
import { BOTTOM_NAV_HEIGHT, HEADER_HEIGHT } from "../../shared/uikit/Rail";
import { ChatBottomBanner } from "../ChatBottomBanner";
import { MessageItem } from "../ChatWindowV2/MessageItem";
import {
  calculateScrollHeight,
  findMissingParticipantIds,
  getMessageHashFromUrl,
  scrollToMessage,
} from "../ChatWindowV2/helpers";
import {
  ChatRoomBody,
  MessageIndicatorButton,
  NewConversation,
  RichTextMessageBox,
} from "../sharedV2";
import "../ChatWindowV2/overrides.scss";

export const SCROLL_WRAPPER_DEFAULT_HEIGHT = HEADER_HEIGHT + BOTTOM_NAV_HEIGHT;

const DEFAULT_ROLES: Record<string, any> = {};

interface EmbeddableMessagesContainerProps {
  chatRoom: ChatRoom;
  chatUserSearchRef?: React.RefObject<HTMLInputElement>;
  displayDayChange?: boolean;
  isChatPreview?: boolean;
  isChatSpace?: boolean;
  isDrawer?: boolean;
  isInCourseSpace?: boolean;
  isLiveStream?: boolean;
  messageBodyClassName?: string;
  roles?: Record<string, any>;
}

export const EmbeddableMessagesContainer = ({
  chatRoom,
  displayDayChange = true,
  isChatPreview,
  isChatSpace,
  isInCourseSpace,
  chatUserSearchRef,
  isLiveStream,
  isDrawer,
  roles = DEFAULT_ROLES,
  messageBodyClassName,
}: EmbeddableMessagesContainerProps) => {
  const messageHashParamsId = getMessageHashFromUrl();
  const [messageHashId, setMessageHashId] = useState(messageHashParamsId);
  const { openThreadsRailbarOnThreadsNotification, closeThreadsRailbar } =
    useThreads();
  const { data: space } = useCurrentSpaceContext();
  const { isViewOnlyMasquerading } = usePunditUserContext();
  const [isUnreadMessageLineDisabled, setIsUnreadMessageLineDisabled] =
    useState(false);
  const { markChatRoomAsRead: markChatRoomMessagesAsRead } =
    useMarkChatRoomAsRead();

  const { refetchSpaceNotifications } = useSpaceSidebarNotifications([
    space?.id,
  ]);

  const { spaces: spaceNotifications } = useSpaceNotificationsState();
  const spaceNotificationDetail = getSpaceNotificationDetail(
    spaceNotifications,
    space?.id,
  );

  const history = useHistory();
  const [isLastPage, setIsLastPage] = useState(true);
  const [isFirstPage, setIsFirstPage] = useState(true);
  const isSmOrMdScreen = useSmOrMdScreenMediaQuery();
  useHideHelpscout();
  const { chatParticipants: participants } = useChatParticipantsApi(
    chatRoom.uuid,
    { shouldDisableCache: true, per_page: 100 },
  );
  const { isMessaging } = useChatRoomContext();

  const {
    uuid,
    last_message: lastMessage,
    current_participant = {},
    pinned_message,
    first_unread_message_id: unreadMessageId,
  } = chatRoom;

  const prevDayRef = createRef();

  const { activeMessageId, hasNextPage, isLoading, isInitialPageLoaded } =
    useChatRoomStoreStates();
  const {
    setChatRoomMessages,
    setActiveMessageId,
    setScrollInfo,
    appendLastMessage,
  } = useChatRoomStoreActions();

  const { chatRoomMessages = [] } = useChatRoomStoreMessages();
  const { calculateScrollInfo, scrollerId: messagesContainerId } =
    useScrollInfo();
  const { fetchChatMessagesFirstPage, adjustScroller, setUnreadMessageFlow } =
    useChatMessagesV2Api();

  const onBookmarkToggle = useBookmarkToggle(
    chatRoomMessages,
    setChatRoomMessages,
  );

  const isNewConversation = !isChatSpace && chatRoomMessages.length === 0;

  const { canSendMessage, canParticipantsMessage } =
    useCanSendMessage(chatRoom);

  const [scrollWrapperHeight, setScrollWrapperHeight] = useState<number>();
  const footerRef = useRef<HTMLDivElement>(null);
  const isInitialPage = isLastPage && isFirstPage;

  useEffect(() => {
    setMessageHashId(messageHashParamsId);
  }, [messageHashParamsId]);

  useEffect(() => {
    closeThreadsRailbar();
  }, [uuid]);

  useEffect(() => {
    void fetchChatMessagesFirstPage(Number(messageHashId) || unreadMessageId);
    setIsFirstPage(true);
    setIsLastPage(true);
  }, [uuid, messageHashId, unreadMessageId]);

  useEffect(() => {
    if (messageHashId && isInitialPage && !isLoading) {
      scrollToMessage(messageHashId);
      openThreadsRailbarOnThreadsNotification({
        messageHashId,
        chatRoomMessages,
        chatRoom,
      } as any);
    }
  }, [messageHashId, isLoading, isInitialPage]);

  const memoizedOrderedMessages = useMemo(() => {
    const uniqMessages = uniqBy(chatRoomMessages, "id");
    return sortBy(uniqMessages, "sent_at");
  }, [chatRoomMessages]);

  useEffect(() => {
    const skipAdjustScroller =
      (isInitialPage && messageHashId) || !isInitialPageLoaded;

    if (!skipAdjustScroller) {
      defer(adjustScroller);
    }
  }, [memoizedOrderedMessages, messageHashId, isInitialPageLoaded]);

  const setLatestMessagetoChatRoomMessages = () => {
    if (hasNextPage) return;
    if (lastMessage && lastMessage.id > 0) {
      appendLastMessage(lastMessage);
    }
  };

  useEffect(() => {
    const calculateAndSetScrollHeight = () => {
      const height: number = calculateScrollHeight({
        searchInputClientHeight: chatUserSearchRef?.current?.clientHeight,
        footerClientHeight: footerRef?.current?.clientHeight,
        isSmOrMdScreen,
        isLiveStream,
        isInCourseSpace,
        isDrawer,
      });
      setScrollWrapperHeight(height);
    };

    calculateAndSetScrollHeight();
    window.addEventListener("resize", calculateAndSetScrollHeight);
    return () =>
      window.removeEventListener("resize", calculateAndSetScrollHeight);
  }, [
    chatUserSearchRef,
    isNewConversation,
    canSendMessage,
    isLiveStream,
    isInCourseSpace,
    isSmOrMdScreen,
    footerRef?.current?.clientHeight,
    isDrawer,
  ]);

  useEffect(() => {
    setIsUnreadMessageLineDisabled(
      !((isChatSpace || isMessaging) && canSendMessage),
    );
  }, [canSendMessage, isChatSpace, isMessaging]);

  const shouldShowLatestMessageBtn = hasNextPage && isInitialPageLoaded;

  const missingParticipantIds = useMemo(
    () => findMissingParticipantIds(memoizedOrderedMessages, participants),
    [memoizedOrderedMessages, participants],
  );

  useUpdateMissingParticipants({
    uuid: chatRoom.uuid,
    ids: missingParticipantIds,
    enabled: Boolean(participants.length && missingParticipantIds.length),
  });

  const redirectAndMarkAsRead = async ({
    shouldAppendLastMessage = false,
  }: {
    shouldAppendLastMessage?: boolean;
  } = {}) => {
    if (
      isChatSpace &&
      spaceNotificationDetail?.unread_content_count > 0 &&
      !isViewOnlyMasquerading
    ) {
      try {
        const response = await markChatRoomMessagesAsRead({
          uuid: chatRoom.uuid,
        });
        if (response) {
          void refetchSpaceNotifications();
        }
      } catch (error) {
        console.error(error);
      }
    }
    if (messageHashId) {
      history.push({});
    } else {
      void fetchChatMessagesFirstPage();
    }
    // Last message needs to be appended when the user sends message from notification landing page
    if (shouldAppendLastMessage) {
      delay(setLatestMessagetoChatRoomMessages, 100);
    }
  };

  const heightUnit = getVerticalHeightUnit();

  const chatRoomData = useMemo(
    () => ({
      uuid: chatRoom.uuid,
      chat_room_kind: chatRoom.chat_room_kind,
      current_participant: current_participant,
      chat_room_participants_count: chatRoom.chat_room_participants_count,
    }),
    [
      chatRoom.uuid,
      chatRoom.chat_room_kind,
      current_participant,
      chatRoom.chat_room_participants_count,
    ],
  );

  const chatRoomWithoutLastMessage = useMemo(
    () => omit(chatRoom, "last_message"),
    [
      chatRoom.uuid,
      chatRoom.chat_room_kind,
      chatRoom.identifier,
      chatRoom.chat_room_participants_count,
      current_participant,
      chatRoom.chat_room_show_history,
      chatRoom.pinned_message,
      chatRoom.first_unread_message_id,
      chatRoom.chat_room_name,
      chatRoom.chat_room_description,
    ],
  );

  return (
    <div
      id="scrollWrapper"
      className="flex grow flex-col overflow-y-auto border-r-0"
      data-testid="chat-room-wrapper"
      {...(scrollWrapperHeight && {
        style: {
          height: `calc(100${heightUnit} - ${scrollWrapperHeight}px)`,
        },
      })}
    >
      {!isInitialPageLoaded ? (
        <div id={messagesContainerId} className="overflow-hidden pt-0">
          <SkeletonLoader variant="chat-room" hideMessageBox />
        </div>
      ) : (
        <>
          {pinned_message && (
            <MessageItem
              message={pinned_message}
              currentParticipant={current_participant}
              isPinned
              roles={roles}
              chatRoom={chatRoomWithoutLastMessage}
              chatThreadsEnabled={!isChatPreview}
              isLiveStream={isLiveStream}
              isPinnedMessageAtTop
              participants={participants}
              messageBodyClassName={messageBodyClassName}
              onBookmarkToggle={onBookmarkToggle}
              setUnreadMessageFlow={setUnreadMessageFlow}
              setScrollInfo={setScrollInfo}
              calculateScrollInfo={calculateScrollInfo}
              activeMessageId={activeMessageId}
              setActiveMessageId={setActiveMessageId}
              isViewOnlyMasquerading={isViewOnlyMasquerading}
            />
          )}
          <ChatRoomBody
            isLastPage={isLastPage}
            isFirstPage={isFirstPage}
            setIsLastPage={setIsLastPage}
            setIsFirstPage={setIsFirstPage}
            componentId={messagesContainerId}
          >
            {memoizedOrderedMessages.map((message: any) => (
              <MessageItem
                key={message.id}
                message={message}
                displayDayChange={displayDayChange}
                ref={prevDayRef}
                currentParticipant={current_participant}
                isInitialPage={isInitialPage}
                isLastMessage={
                  last<ChatRoomMessage>(chatRoomMessages)?.id === message.id
                }
                messageHashId={messageHashId}
                roles={roles}
                isPinned={pinned_message?.id === message.id}
                disableUnreadMessageLine={isUnreadMessageLineDisabled}
                unreadMessageId={unreadMessageId}
                chatRoom={chatRoomWithoutLastMessage}
                chatThreadsEnabled={!isChatPreview}
                isLiveStream={isLiveStream}
                participants={participants}
                messageBodyClassName={messageBodyClassName}
                onBookmarkToggle={onBookmarkToggle}
                setUnreadMessageFlow={setUnreadMessageFlow}
                activeMessageId={activeMessageId}
                setActiveMessageId={setActiveMessageId}
                setScrollInfo={setScrollInfo}
                calculateScrollInfo={calculateScrollInfo}
                isViewOnlyMasquerading={isViewOnlyMasquerading}
              />
            ))}
          </ChatRoomBody>
        </>
      )}
      {shouldShowLatestMessageBtn && (
        <div className="relative bottom-8 m-auto h-0">
          <MessageIndicatorButton
            label={t("messaging.latest_messages")}
            onClick={() => {
              void redirectAndMarkAsRead();
            }}
          />
        </div>
      )}
      {canSendMessage ? (
        <div
          ref={footerRef}
          className={classNames(
            "bg-primary fixed z-[9] w-full md:static md:bottom-auto md:z-auto md:w-auto md:bg-auto",
            {
              "bottom-0": !isNewConversation || isLiveStream,
              "bottom-16": (isNewConversation && !isLiveStream) || isChatSpace,
              "rounded-b-lg": isLiveStream,
            },
          )}
        >
          {!isLoading && isNewConversation && (
            <NewConversation
              isLiveStream={isLiveStream}
              isInCourseSpace={isInCourseSpace}
              isMessaging={isMessaging}
              chatRoomUuid={chatRoom.uuid}
            />
          )}
          <RichTextMessageBox
            spaceId={space?.id}
            chatRoomData={chatRoomData}
            onClick={() => {
              if (messageHashId || hasNextPage) {
                void redirectAndMarkAsRead({ shouldAppendLastMessage: true });
              }
            }}
            isChatPreview={isChatPreview}
            isLiveStream={isLiveStream}
            isChatSpace={isChatSpace}
            isInCourseSpace={isInCourseSpace}
            isLoading={!isInitialPageLoaded || isLoading}
            participants={participants}
          />
        </div>
      ) : (
        <div
          className="fixed bottom-0 z-[9] h-24 w-full pb-1 pt-2 md:static md:z-auto md:w-auto"
          ref={footerRef}
        >
          <ChatBottomBanner>
            {canParticipantsMessage
              ? t("community_member.join_space_to_message")
              : t("community_member.messaging_disabled")}
          </ChatBottomBanner>
        </div>
      )}
    </div>
  );
};
